// Generic, memoized line/area time-series chart. Replaces the hand-rolled
// LineChart/AreaChart/ComposedChart blocks that were duplicated across pages.
//
// Performance:
//   • React.memo — skips re-render when props are referentially stable.
//   • useMemo data pipeline — downsamples dense series (LTTB) once per change.
//   • animation disabled + debounced ResponsiveContainer (ChartFrame).
//   • area gradients are defined once per series key.

import { memo, useMemo } from "react";
import {
  ComposedChart,
  Area,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
} from "recharts";

import { ChartFrame } from "@/components/charts/ChartFrame";
import { ChartLegend } from "@/components/charts/ChartLegend";
import { downsampleLTTB } from "@/components/charts/downsample";
import { usePhone } from "@/app/console/useViewport";
import {
  ANIMATION_ACTIVE,
  AXIS_TICK,
  GRID_PROPS,
  LEGEND_STYLE,
  TOOLTIP_CONTENT_STYLE,
  TOOLTIP_LABEL_STYLE,
} from "@/components/charts/theme";

export interface SeriesDef {
  key: string;
  name: string;
  color: string;
  kind?: "line" | "area";
  dashed?: boolean;
  strokeWidth?: number;
  fillOpacity?: number;
}

export interface TimeSeriesChartProps {
  // Rows are accessed by string key (xKey / series keys); `unknown[]` keeps it
  // compatible with the codegen'd interface types that lack index signatures.
  data: readonly unknown[];
  series: ReadonlyArray<SeriesDef>;
  xKey: string;
  height?: number;
  legend?: boolean;
  grid?: boolean;
  xTickFormatter?: (value: unknown) => string;
  yTickFormatter?: (value: number) => string;
  yWidth?: number;
  minTickGap?: number;
  tooltipFormatter?: (value: unknown, name: string) => [string, string] | string;
  tooltipLabelFormatter?: (label: unknown) => string;
  /** Downsample target; series longer than this are reduced with LTTB. */
  maxPoints?: number;
  /** Full-screen heading (defaults to the enclosing Card's title). */
  title?: string;
  /** Expand affordance + tap-to-fullscreen on phones (default on). */
  expandable?: boolean;
}

function TimeSeriesChartBase({
  data,
  series,
  xKey,
  height = 220,
  legend = true,
  grid = true,
  xTickFormatter,
  yTickFormatter,
  yWidth = 56,
  minTickGap = 30,
  tooltipFormatter,
  tooltipLabelFormatter,
  maxPoints = 600,
  title,
  expandable,
}: TimeSeriesChartProps) {
  // Phones: thin the x ticks (≤ 5–6 on a 340 px plot) and always keep the ends.
  const phone = usePhone();
  const chartData = useMemo<Record<string, unknown>[]>(() => {
    const rows = data as Record<string, unknown>[];
    const primary = series[0]?.key;
    if (!primary || rows.length <= maxPoints) return rows;
    return downsampleLTTB(rows, maxPoints, (d) => Number(d[primary]) || 0);
  }, [data, series, maxPoints]);

  const areaSeries = useMemo(() => series.filter((s) => s.kind === "area"), [series]);

  return (
    <ChartFrame height={height} title={title} expandable={expandable}>
      <ComposedChart data={chartData} margin={{ top: 8, right: 12, left: 0, bottom: 0 }}>
        {areaSeries.length > 0 && (
          <defs>
            {areaSeries.map((s) => (
              <linearGradient key={s.key} id={`grad-${s.key}`} x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor={s.color} stopOpacity={s.fillOpacity ?? 0.25} />
                <stop offset="95%" stopColor={s.color} stopOpacity={0} />
              </linearGradient>
            ))}
          </defs>
        )}
        {grid && <CartesianGrid {...GRID_PROPS} />}
        <XAxis
          dataKey={xKey}
          tick={AXIS_TICK}
          tickLine={false}
          minTickGap={phone ? Math.max(minTickGap, 56) : minTickGap}
          interval={phone ? "preserveStartEnd" : undefined}
          tickFormatter={xTickFormatter as ((value: string) => string) | undefined}
        />
        <YAxis
          tick={AXIS_TICK}
          tickLine={false}
          width={yWidth}
          tickFormatter={yTickFormatter}
        />
        <Tooltip
          contentStyle={TOOLTIP_CONTENT_STYLE}
          labelStyle={TOOLTIP_LABEL_STYLE}
          formatter={tooltipFormatter as never}
          labelFormatter={tooltipLabelFormatter as never}
        />
        {legend && <Legend wrapperStyle={LEGEND_STYLE} content={<ChartLegend />} />}
        {series.map((s) =>
          s.kind === "area" ? (
            <Area
              key={s.key}
              type="monotone"
              dataKey={s.key}
              name={s.name}
              stroke={s.color}
              strokeWidth={s.strokeWidth ?? 2}
              fill={`url(#grad-${s.key})`}
              dot={false}
              isAnimationActive={ANIMATION_ACTIVE}
            />
          ) : (
            <Line
              key={s.key}
              type="monotone"
              dataKey={s.key}
              name={s.name}
              stroke={s.color}
              strokeWidth={s.strokeWidth ?? 2}
              strokeDasharray={s.dashed ? "5 3" : undefined}
              dot={false}
              activeDot={{ r: 4 }}
              isAnimationActive={ANIMATION_ACTIVE}
            />
          ),
        )}
      </ComposedChart>
    </ChartFrame>
  );
}

export const TimeSeriesChart = memo(TimeSeriesChartBase);
